import cx from 'classnames'
import { lazy, Suspense, Fragment, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { matchI18nPathList, useMatchList } from 'features/i18n/router'
import { Outlet, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

import { isMobileDevice } from 'lib/device'
import { ROUTE_PATH } from 'lib/paths'
import { useResponsive } from 'hooks'
import { selectBreadcrumb } from 'store/selectors'

const Modal = lazy(() => import('components/Modal/PageModal'))
const SideBar = lazy(() => import('components/SideBar'))
import Header from 'components/Header'
import InviteHeader from 'features/InvitePage/components/Header'
import SideBarSkeletonLoading from 'components/SideBar/SideBarSkeletonLoading'
import PushNotification from 'features/PushNotification/PushNotification'
import Footer from 'components/Footer'
import PaymentFooter from 'features/Payment/components/PaymentFooter'
import { Breadcrumbs } from 'components'

const MainLayout = () => {
  const { isDesktop } = useResponsive()
  const location = useLocation()
  const t = useTranslation()

  const isInvitePage = useMatchList([
    ROUTE_PATH.USER_INVITE(),
    ROUTE_PATH.GROUP_INVITE(),
  ])
  const isVipPaymentPage = useMatchList([ROUTE_PATH.VIP_PURCHASE], true)

  const breadcrumbs = useSelector(selectBreadcrumb)

  const shouldShowSideBar = useMemo(() => {
    if (!isDesktop) return false

    // Path is not in the list of hidden sidebar
    return !matchI18nPathList(
      [
        ROUTE_PATH.LOGIN,
        ROUTE_PATH.EMPL_DETAILS,
        ROUTE_PATH.SETTING,
        ROUTE_PATH.EMAIL_CONFIRMED,
        ROUTE_PATH.PHONE_VERIFICATION,
        ROUTE_PATH.VIP_PURCHASE,
        ROUTE_PATH.TIMELINE_TESTING_TOOLS,
        ROUTE_PATH.TESTNET_AIRDROP,
        ROUTE_PATH.AUTHORIZE_WALLET,
        ROUTE_PATH.GROUPS_RANKING,
        ROUTE_PATH.CHAT,
      ],
      { startsWith: true }
    )
  }, [isDesktop, location.pathname, t.i18n.language])

  return (
    <Fragment>
      <div className="Main">
        {isInvitePage && isMobileDevice ? <InviteHeader /> : <Header />}

        <div
          className={cx(
            'Main__content',
            !shouldShowSideBar && 'Main__content--no-side-bar'
          )}
        >
          <div className="container">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </div>

          <div className="container">
            <main className="Content">
              <Outlet />
            </main>

            {shouldShowSideBar && (
              <Suspense fallback={<SideBarSkeletonLoading />}>
                <SideBar />
              </Suspense>
            )}
          </div>
        </div>

        {isInvitePage && <Footer />}

        {isVipPaymentPage && <PaymentFooter />}

        <Suspense fallback={<div />}>
          <Modal />
        </Suspense>

        <PushNotification />
      </div>
    </Fragment>
  )
}

export default MainLayout
